import { FunctionComponent, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { Alert } from 'react-bootstrap'

import Page from 'components/ui/page'
import PageHeader from 'components/ui/pageHeader'
import ConfirmationModal from '../../components/modal/confirmationModal'
import HospitalForm from './components/hospitalForm'
import HospitalAdminsSection from './components/hospitalAdminsSection'
import HospitalAdminModal from './components/hospitalAdminModal'
import * as api from './api.client'
import { AdminHospital } from './types'
import { removeAlphabeticCharacteres } from '../../utilities/string.util'
import { AppMessage } from '../../utilities/messages'

const EditHospital: FunctionComponent = () => {
  const { id } = useParams<{ id: string }>()
  const [hospitalId, setHospitalId] = useState<number>()
  const [hospital, setHospital] = useState<AdminHospital>()
  const [showAddAdmin, setShowAddAdmin] = useState(false)
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false)
  const [removingHospitalAdminlId, setRemovingHospitalAdminlId] =
    useState<number>()
  const [showFormErrors, setShowFormErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [appMessage, setAppMessage] = useState<AppMessage>({ show: false })

  useEffect(() => {
    getHospitalById(Number.parseInt(id as string))
  }, [id])

  const getHospitalById = async (id: number) => {
    const hospital = await api.getHospitalById(id)
    setHospital(hospital)
    setHospitalId(id)
  }

  if (!hospital || !hospitalId) {
    return null
  }

  const updateHospital = async (event: React.ChangeEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formIsValid = event.currentTarget.checkValidity()

    if (!formIsValid) {
      setShowFormErrors(true)
      return
    }

    setSaving(true)

    try {
      await api.updateHospital(hospital.id, {
        ...hospital,
        cnpj: removeAlphabeticCharacteres(hospital.cnpj),
      })
      setAppMessage({ show: false })
    } catch (error) {
      setAppMessage({
        show: true,
        success: false,
        message: 'Erro ao atualizar os dados do hospital.',
      })
    } finally {
      setSaving(false)
    }
  }

  // Errors propagate to the modal, which shows them and stays open.
  const addHospitalAdmin = async (admin: { name: string; email: string }) => {
    await api.addHospitalAdmin({ ...admin, hospitalId })
    await getHospitalById(hospitalId)

    setShowAddAdmin(false)
    setAppMessage({
      show: true,
      success: true,
      message:
        'Admin adicionado com sucesso. Enviamos um email para o usuário solicitando a confirmação do cadastro.',
    })
  }

  const removeHospitalAdmin = async () => {
    if (!removingHospitalAdminlId) {
      return
    }

    try {
      await api.removeHospitalAdmin(removingHospitalAdminlId)
      await getHospitalById(hospitalId)
    } catch (error) {
      setAppMessage({
        show: true,
        success: false,
        message: 'Erro ao remover o admin.',
      })
    } finally {
      setShowRemoveConfirmation(false)
      setRemovingHospitalAdminlId(undefined)
    }
  }

  return (
    <Page>
      {showRemoveConfirmation && (
        <ConfirmationModal
          message="Você deseja realmente remover o admin?"
          btnMessage="Remover"
          btnVariant="outline-danger"
          onClose={() => setShowRemoveConfirmation(false)}
          onConfirmation={() => removeHospitalAdmin()}
        />
      )}

      {showAddAdmin && (
        <HospitalAdminModal
          onClose={() => setShowAddAdmin(false)}
          onAdd={addHospitalAdmin}
        />
      )}

      <PageHeader
        back={{ to: '/hospitals', label: 'Hospitais' }}
        title="Editar hospital"
        subtitle="Dados cadastrais e administradores do hospital."
      />

      {appMessage.show && (
        <Alert
          variant={appMessage.success ? 'success' : 'danger'}
          onClose={() => setAppMessage({ show: false })}
          dismissible
        >
          {appMessage.message}
        </Alert>
      )}

      <HospitalForm
        hospital={hospital}
        onChange={(changes) => setHospital({ ...hospital, ...changes })}
        onSubmit={updateHospital}
        validated={showFormErrors}
        saving={saving}
      />

      <div style={{ maxWidth: '48rem' }}>
        <HospitalAdminsSection
          admins={hospital.hospitalAdmins}
          onAdd={() => setShowAddAdmin(true)}
          onRemove={(adminId) => {
            setShowRemoveConfirmation(true)
            setRemovingHospitalAdminlId(adminId)
          }}
        />
      </div>
    </Page>
  )
}

export default EditHospital
